import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Ban,
  FileText,
  GitBranchPlus,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  Undo2,
  Upload,
} from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Textarea } from "@/shared/components/ui/textarea";
import { useToast } from "@/shared/hooks/use-toast";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { DataPagination } from "@/shared/components/DataPagination";
import { usePagination } from "@/shared/hooks/usePagination";
import { invalidarSkills } from "@/features/Skills/utils/invalidate";
import { SkillsAdminService, type SkillAdminEntry } from "../services/skillsAdminService";
import { SkillPublishDialog } from "./SkillPublishDialog";
import { textoDaSkill } from "@/features/Skills/utils/text";
import { MagicFillProvider } from "@/shared/contexts/MagicFillContext";

/**
 * O catálogo de skills da plataforma, na área de superadministração
 * (AB#85634, AC1).
 *
 * Vive aqui, e não numa seção própria, porque Templates já é o lugar dos
 * catálogos que a plataforma publica e a organização instancia — as abas de
 * Agents e Integrations estão reservadas ali pelo mesmo motivo.
 *
 * **Não há equivalente desta tela para a organização**, e é deliberado: lá a
 * grade de `/skills` já É o catálogo, e cada skill já é um cartão. Uma segunda
 * lista mostrando as mesmas skills seria a mesma informação duas vezes.
 *
 * O que esta tela mostra e a loja **não** mostra: a skill inativa, a skill core
 * (que não se instala) e o número de instalações. As três respondem à pergunta
 * de quem cura, não à de quem consome — e a última é a que diz se inativar uma
 * skill vai atingir alguém.
 */
export function SkillTemplatesSettings() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [publicando, setPublicando] = useState(false);
  /** A skill de que se está registrando uma versão nova; nulo = skill nova. */
  const [novaVersaoDe, setNovaVersaoDe] = useState<SkillAdminEntry | null>(null);
  /** A skill cuja versão mais recente espera confirmação para ir ao ar. */
  const [publicandoVersao, setPublicandoVersao] = useState<SkillAdminEntry | null>(null);

  /** A skill que espera o motivo para ser inativada. */
  const [inativando, setInativando] = useState<SkillAdminEntry | null>(null);
  const [motivo, setMotivo] = useState("");

  const { data: skills, isLoading } = useQuery({
    queryKey: ["skills_admin_catalog"],
    queryFn: () => SkillsAdminService.list(),
  });

  const filtered = useMemo(() => {
    const termo = search.trim().toLowerCase();
    if (!termo) return skills ?? [];

    return (skills ?? []).filter((skill) =>
      [skill.name, skill.title ?? "", skill.description, skill.provider ?? "", ...skill.facets]
        .join(" ")
        .toLowerCase()
        .includes(termo),
    );
  }, [skills, search]);

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } =
    usePagination<SkillAdminEntry>({ initialPageSize: 10 });

  const pagina = useMemo(() => paginateData(filtered), [filtered, paginateData]);

  /** Tira a skill do ar. As versões continuam no catálogo. */
  const despublicar = useMutation({
    mutationFn: (skill: SkillAdminEntry) => SkillsAdminService.unpublish(skill.name),
    onSuccess: () => {
      invalidarSkills(queryClient);
      toast({ title: t("skills.publish.unpublishedTitle") });
    },
    onError: (falha) => {
      toast({
        variant: "destructive",
        title: t("skills.publish.unpublishFailed"),
        description: falha instanceof Error ? falha.message : undefined,
      });
    },
  });

  /** Coloca no ar a versão mais recente da skill. */
  const publicar = useMutation({
    mutationFn: (skill: SkillAdminEntry) => SkillsAdminService.publishVersion(skill.name),
    onSuccess: (resultado, skill) => {
      invalidarSkills(queryClient);
      toast({
        title: t("skills.publish.publishedTitle"),
        // Quantas foram levadas junto, e quantas ficaram: publicar deixou de
        // ser um ato só sobre o catálogo.
        description: [
          t("skills.publish.publishedDescription", {
            skill: skill.title || skill.name,
            version: resultado.version,
          }),
          resultado.moved > 0 ? t("skills.publish.carried", { count: resultado.moved }) : null,
          resultado.kept > 0 ? t("skills.publish.keptBehind", { count: resultado.kept }) : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
    },
    onError: (falha) => {
      toast({
        variant: "destructive",
        title: t("skills.publish.publishFailed"),
        description: falha instanceof Error ? falha.message : undefined,
      });
    },
  });

  const alterar = useMutation({
    mutationFn: ({
      name,
      mudancas,
    }: {
      name: string;
      mudancas: Parameters<typeof SkillsAdminService.update>[1];
    }) => SkillsAdminService.update(name, mudancas),
    onSuccess: () => {
      invalidarSkills(queryClient);
      setInativando(null);
      setMotivo("");
      toast({ title: t("skills.admin.saved") });
    },
    onError: (falha) => {
      toast({
        variant: "destructive",
        title: t("skills.admin.saveFailed"),
        description: falha instanceof Error ? falha.message : undefined,
      });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          {/* `z-10`: o Input se envolve numa div e vem depois no DOM, com fundo
              opaco — sem isto o ícone fica atrás dele. */}
          <Search className="pointer-events-none absolute left-2.5 top-2.5 z-10 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setCurrentPage(1);
            }}
            placeholder={t("skills.admin.searchPlaceholder")}
            className="pl-8"
          />
        </div>

        <Button size="sm" onClick={() => setPublicando(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t("skills.publish.action")}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("skills.admin.columnName")}</TableHead>
              <TableHead>{t("skills.admin.columnScope")}</TableHead>
              <TableHead className="w-24">{t("skills.admin.columnVersion")}</TableHead>
              <TableHead className="w-28">{t("skills.admin.columnInstalls")}</TableHead>
              <TableHead className="w-32">{t("skills.admin.columnStatus")}</TableHead>
              <TableHead className="w-16 text-right">{t("skills.admin.columnActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, linha) => (
                <TableRow key={linha}>
                  {Array.from({ length: 6 }).map((__, coluna) => (
                    <TableCell key={coluna}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pagina.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  {search ? t("skills.admin.emptySearch") : t("skills.admin.empty")}
                </TableCell>
              </TableRow>
            ) : (
              pagina.map((skill) => (
                <TableRow
                  key={skill.name}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/skills/${encodeURIComponent(skill.name)}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {textoDaSkill(skill.title, skill.title_i18n, i18n.language) || skill.name}
                      </span>
                      {skill.is_core && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="gap-1 text-[11px]">
                              <Sparkles className="h-3 w-3" />
                              {t("skills.admin.core")}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t("skills.admin.coreHint")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{skill.name}</p>
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline" className="text-[11px] font-normal">
                      {t(`skills.scope.${skill.install_scope}`)}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-sm">
                    {skill.version === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <span>
                          v{skill.version}
                          <span className="ml-1 text-xs text-muted-foreground">
                            {t("skills.admin.ofVersions", { count: skill.version_count })}
                          </span>
                        </span>

                        {/* Um ponto ao lado do número, e não um segundo selo na
                            situação: o aviso é justamente sobre a distância
                            entre a versão do ar e a última registrada, que é o
                            que esta célula já diz. */}
                        {skill.version_count > skill.version && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {t("skills.publish.pendingHint", {
                                count: skill.version_count - skill.version,
                              })}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    )}
                  </TableCell>

                  {/* A contagem é o que diz se inativar atinge alguém. Skill core
                      não tem linha de instalação por desenho — mostrar "0" ali
                      sugeriria que ninguém a usa, quando ela é servida a todos. */}
                  <TableCell className="text-sm">
                    {skill.is_core ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-muted-foreground">
                            {t("skills.admin.everyone")}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {t("skills.admin.everyoneHint")}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      skill.installation_count
                    )}
                  </TableCell>

                  {/* Três estados, e não dois: uma skill ativa sem versão
                      corrente não é servida a ninguém, e mostrá-la como
                      "Publicada" seria mentir sobre o que as organizações
                      recebem. Desde que o rascunho saiu (AB#85637), só se chega
                      a este estado DESPUBLICANDO — nascer invisível não existe
                      mais. */}
                  <TableCell>
                    {skill.status === "active" && skill.version === null ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="text-[11px]">
                            {t("skills.publish.offAir")}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {t("skills.publish.offAirHint")}
                        </TooltipContent>
                      </Tooltip>
                    ) : skill.status === "active" ? (
                      <Badge variant="secondary" className="text-[11px]">
                        {t("skills.admin.statusActive")}
                      </Badge>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="text-[11px] text-muted-foreground">
                            {t("skills.admin.statusInactive")}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {skill.inactive_reason || t("skills.admin.noReason")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Promover a core NÃO é um interruptor de lista: desde que o
                      bloqueio por organização saiu, core passou a ser
                      OBRIGATÓRIA para todas, e nenhuma pode recusá-la. Fica
                      atrás de um menu, e a promoção ainda pede confirmação.
                      Despromover não pergunta nada — só devolve a skill à loja. */}
                  <TableCell className="text-right" onClick={(evento) => evento.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* A linha inteira também navega, mas isso não se
                            descobre olhando — o item explícito é o caminho que
                            a pessoa encontra. */}
                        <DropdownMenuItem
                          onSelect={() =>
                            navigate(`/admin/skills/${encodeURIComponent(skill.name)}`)
                          }
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          {t("skills.admin.details")}
                        </DropdownMenuItem>

                        {/* Publicar aparece quando há versão registrada que
                            ninguém recebe — a primeira de uma skill nova, ou uma
                            mais nova que a que está no ar. */}
                        {skill.version_count > (skill.version ?? 0) && (
                          <DropdownMenuItem
                            disabled={publicar.isPending}
                            onSelect={() => setPublicandoVersao(skill)}
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            {t("skills.publish.publishLatest")}
                          </DropdownMenuItem>
                        )}

                        {/* Despublicar só faz sentido para o que ESTÁ no ar. */}
                        {skill.version !== null && (
                          <DropdownMenuItem
                            disabled={despublicar.isPending}
                            onSelect={() => despublicar.mutate(skill)}
                          >
                            <Undo2 className="mr-2 h-4 w-4" />
                            {t("skills.publish.unpublish")}
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuItem
                          onSelect={() => {
                            setNovaVersaoDe(skill);
                            setPublicando(true);
                          }}
                        >
                          <GitBranchPlus className="mr-2 h-4 w-4" />
                          {t("skills.publish.newVersion")}
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        {/* Inativar pede o motivo; reativar não pede nada —
                            devolver ao catálogo não precisa de explicação. */}
                        {skill.status === "active" ? (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => {
                              setMotivo("");
                              setInativando(skill);
                            }}
                          >
                            <Ban className="mr-2 h-4 w-4" />
                            {t("skills.admin.inactivate")}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            disabled={alterar.isPending}
                            onSelect={() =>
                              alterar.mutate({ name: skill.name, mudancas: { status: "active" } })
                            }
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            {t("skills.admin.reactivate")}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Um diálogo só, e não dois: registrar uma skill e registrar uma versão
          dela são o MESMO formulário — o que muda é abrir com o pacote e o
          cadastro que já estão no catálogo. */}
      {/* Publicar é o único ato com consequência fora desta tela, e por isso é
          o único que confirma: a tela sabe quantas instalações vão receber o
          aviso de versão nova, e decidir sem esse número é decidir no escuro. */}
      <AlertDialog
        open={!!publicandoVersao}
        onOpenChange={(aberto) => !aberto && setPublicandoVersao(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("skills.publish.confirmTitle", { version: publicandoVersao?.version_count })}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">{t("skills.publish.confirmBody")}</span>
              <span className="block">
                {publicandoVersao?.installation_count
                  ? t("skills.publish.confirmInstalls", {
                      count: publicandoVersao.installation_count,
                    })
                  : t("skills.publish.confirmNoInstalls")}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (publicandoVersao) publicar.mutate(publicandoVersao);
                setPublicandoVersao(null);
              }}
            >
              {t("skills.publish.publishLatest")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* O provider do Magic Fill embrulha a JANELA de fora: é ele que guarda
          quais campos estão esperando sugestão, e o `Input` troca a varinha por
          um spinner lendo daí. Dentro do próprio diálogo ele não serviria — o
          componente que precisa do estado é justamente o que o renderizaria. */}
      <MagicFillProvider>
        <SkillPublishDialog
          open={publicando}
          skill={novaVersaoDe}
          onOpenChange={(aberto) => {
            setPublicando(aberto);
            if (!aberto) setNovaVersaoDe(null);
          }}
        />
      </MagicFillProvider>

      <Dialog open={!!inativando} onOpenChange={(aberto) => !aberto && setInativando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("skills.admin.inactivate")}</DialogTitle>
            <DialogDescription>
              {t("skills.admin.inactivateHint", {
                count: inativando?.installation_count ?? 0,
              })}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            rows={3}
            placeholder={t("skills.admin.reasonPlaceholder")}
          />
          <p className="text-xs text-muted-foreground">{t("skills.admin.reasonRequired")}</p>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setInativando(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={alterar.isPending || !motivo.trim()}
              onClick={() =>
                inativando &&
                alterar.mutate({
                  name: inativando.name,
                  mudancas: { status: "inactive", inactive_reason: motivo.trim() },
                })
              }
            >
              {t("skills.admin.inactivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {filtered.length > 0 && (
        <DataPagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filtered.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
